
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import LoginForm from "./LoginForm";
import SignupForm from "./SignupForm";
import { getUserLocationFromIP } from "@/utils/geo-location";
import { useLocation } from "react-router-dom";
import { Plane, CheckCircle, DollarSign } from "lucide-react";

export type AuthFormProps = {
  type: "login" | "signup";
  onSubmit: (data: any) => Promise<any>;
  isLoading?: boolean;
  onToggleForm?: () => void;
};

const AuthForm = ({ type, onSubmit, isLoading = false, onToggleForm }: AuthFormProps) => {
  const { toast } = useToast();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    country: "",
  });
  const [locationDetected, setLocationDetected] = useState(false);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Flight compensation is disabled - this check is no longer needed
  const isFromFlightCompensation = false;

  // Detect location for signup
  useEffect(() => {
    if (type === "signup" && !formData.country) {
      const detectLocation = async () => {
        try {
          const locationData = await getUserLocationFromIP();
          if (locationData.country) {
            setFormData(prevData => ({
              ...prevData,
              country: locationData.country || ""
            }));
            setLocationDetected(true);
          }
        } catch (error) {
          console.error("Error detecting location during form load:", error);
        }
      };
      
      detectLocation();
    }
  }, [type, formData.country]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (type === "signup") {
      if (!formData.fullName.trim()) {
        errors.fullName = "Full name is required";
      } else if (formData.fullName.trim().length < 2) {
        errors.fullName = "Full name must be at least 2 characters";
      }
    }
    
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Focus on first field with error
      const firstErrorField = Object.keys(errors)[0];
      const fieldElement = document.getElementById(firstErrorField);
      if (fieldElement) {
        fieldElement.focus();
      }
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await onSubmit(formData);
      
      // Check for error specifically for signup
      if (type === "signup" && result && result.error) {
        const error = result.error;
        console.error("Signup error:", error);
        
        // Handle specific error messages from Supabase
        if (error.message?.includes("already registered") || error.message?.includes("already in use") || error.message?.includes("user_already_exists")) {
          setFormErrors({ email: "This email is already registered" });
        } else if (error.message?.includes("Password should be at least")) {
          setFormErrors({ password: "Password must be at least 6 characters" });
        } else if (error.message?.includes("Invalid email")) {
          setFormErrors({ email: "Please enter a valid email address" });
        } else {
          toast({
            title: "Registration failed",
            description: error.message || "An error occurred. Please try again.",
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      
      // Handle specific error messages from Supabase
      if (error.message?.includes("already registered") || error.message?.includes("user_already_exists")) {
        setFormErrors({ email: "This email is already registered" });
      } else if (error.message?.includes("Invalid login") || error.message?.includes("Invalid credentials")) {
        setFormErrors({ 
          email: "Invalid email or password",
          password: "Invalid email or password"
        });
      } else if (error.message?.includes("Email not confirmed")) {
        toast({
          title: "Email not confirmed",
          description: "Please check your email and click the confirmation link before signing in.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "An error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use the isLoading prop from props and local isSubmitting state
  const buttonIsLoading = isLoading || isSubmitting;

  return (
    <div className="w-full max-w-md mx-auto space-y-4">
      {/* Benefits notification for signup, especially from flight compensation */}
      {type === "signup" && (
        <Alert className="border-primary/20 bg-primary/5">
          <CheckCircle className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm">
            <strong className="text-primary">Join SnapRefund and get instant access to:</strong>
            <ul className="mt-2 space-y-1 text-xs">
              <li className="flex items-center gap-1 text-primary">
                <Plane className="h-3 w-3 text-primary" />
                <span className="text-primary">Free flight delay & cancellation eligibility checks</span>
              </li>
              <li className="flex items-center gap-1 text-primary">
                <DollarSign className="h-3 w-3 text-primary" />
                <span className="text-primary">AI-powered refund request generation</span>
              </li>
              <li className="flex items-center gap-1 text-primary">
                <CheckCircle className="h-3 w-3 text-primary" />
                <span className="text-primary">Track all your claims in one dashboard</span>
              </li>
            </ul>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Flight compensation specific message */}
      {type === "signup" && (
        <Alert className="border-blue-200 bg-blue-50">
          <Plane className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800">
            <strong>Check your flight compensation eligibility for free!</strong>{" "}
            Create your account to get started with our AI-powered flight claims tool.
          </AlertDescription>
        </Alert>
      )}
      
      <Card className="w-full overflow-hidden glass-card animate-fade-in">
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl font-medium">
            {type === "login" ? "Welcome back" : "Create your account"}
          </CardTitle>
          <CardDescription>
            {type === "login" 
              ? "Enter your credentials to sign in to your account" 
              : "Fill in your details to get started with SnapRefund"}
          </CardDescription>
        </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {type === "login" ? (
            <LoginForm 
              formData={formData} 
              handleChange={handleChange} 
              isLoading={buttonIsLoading}
              formErrors={formErrors}
            />
          ) : (
            <SignupForm 
              formData={formData} 
              handleChange={handleChange} 
              isLoading={buttonIsLoading}
              formErrors={formErrors}
              locationDetected={locationDetected}
            />
          )}
        </form>
      </CardContent>
      <CardFooter className="flex justify-center border-t bg-secondary/30 py-4">
        <p className="text-sm text-muted-foreground">
          {type === "login" ? "Don't have an account?" : "Already have an account?"}
          <button
            type="button"
            onClick={onToggleForm}
            className="ml-1 text-primary hover:underline font-medium cursor-pointer bg-transparent border-none"
          >
            {type === "login" ? "Sign Up" : "Sign In"}
          </button>
        </p>
      </CardFooter>
    </Card>
    </div>
  );
};

export default AuthForm;
